import { AddressInputType } from '@domain/addresses/types/types';

/**
 * Extracts a specific component value from Google Maps address components.
 *
 * @param {google.maps.GeocoderAddressComponent[]} components - Array of address components from Google Places API.
 * @param {string} type - The component type to find (e.g., 'locality', 'country').
 * @param {'long_name' | 'short_name'} style - Whether to return 'long_name' or 'short_name'.
 *
 * @returns {string} The component value, or empty string if not found.
 */
export const getAddressComponentValue = (
  components: google.maps.GeocoderAddressComponent[] = [],
  type: string,
  style: 'long_name' | 'short_name' = 'long_name',
): string => {
  const component = components.find((c) => c.types.includes(type));
  return component?.[style] || '';
};

/**
 * Transforms a Google Places result into an AddressInputType.
 *
 * @param {google.maps.places.PlaceResult} details - The place result from Google Places API.
 * @param {string} [address2] - Optional address line 2 override.
 *
 * @returns {AddressInputType} Transformed address input which can be saved to a supplier restaurant
 */
export const getAddressInputFromPlaceResult = (
  details: google.maps.places.PlaceResult,
  address2?: string,
): AddressInputType => {
  const {
    address_components: components = [],
    formatted_address: address = '',
    geometry,
  } = details;

  const location = geometry?.location as unknown as google.maps.LatLngLiteral;
  return {
    address,
    address2:
      address2 ||
      getAddressComponentValue(components, 'subpremise') ||
      undefined,
    city:
      getAddressComponentValue(components, 'locality') ||
      getAddressComponentValue(components, 'sublocality'),
    countryCode: getAddressComponentValue(components, 'country', 'short_name'),
    latitude: location.lat,
    longitude: location.lng,
    region: getAddressComponentValue(components, 'administrative_area_level_1'),
  };
};
